// Running the safe built-in actions of a form: submit the answers (JSON, CSV, FDF, XFDF, an HTML form or the whole PDF), set a field's value from a
// small expression, play a beep. The request for a submit is built without any I/O so it can be checked; the fill ID, fingerprint and time travel with
// the answers because the caller passes the sealed pairs.

import { writeCsv, type SubmitSpec } from "@fg/web-core/pdf";

export interface BuiltRequest { method: "GET" | "POST"; url: string; headers: Array<[string, string]>; body?: string | Blob }

const pct = (s: string) => encodeURIComponent(s).replace(/[!'()*]/g, (c) => "%" + c.charCodeAt(0).toString(16).toUpperCase());
const xml = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const pdfStr = (s: string) => "(" + [...s].map((c) => (c === "(" || c === ")" || c === "\\" ? "\\" + c : c)).join("") + ")";

export function encodePairs(pairs: Array<[string, string]>, format: "HTML" | "XFDF" | "FDF"): string {
    if (format === "HTML") return pairs.map(([k, v]) => `${pct(k)}=${pct(v)}`).join("&");
    if (format === "XFDF") return `<?xml version="1.0" encoding="UTF-8"?>\n<xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve"><fields>${pairs.map(([k, v]) => `<field name="${xml(k)}"><value>${xml(v)}</value></field>`).join("")}</fields></xfdf>\n`;
    return `%FDF-1.2\n1 0 obj\n<</FDF<</Fields[${pairs.map(([k, v]) => `<</T${pdfStr(k)}/V${pdfStr(v)}>>`).join("")}]>>>>\nendobj\ntrailer\n<</Root 1 0 R>>\n%%EOF\n`;
}

/** One header row and one data row; a leading = + - @ would run as a formula in a spreadsheet, so it is kept as text. */
export function csvRow(pairs: Array<[string, string]>): string {
    const safe = (s: string) => (/^[=+\-@]/.test(s) ? `'${s}` : s);
    return writeCsv([pairs.map(([k]) => safe(k)), pairs.map(([, v]) => safe(v))]);
}

/** The request a Submit action makes. `wholePdf` is the sealed file for the Whole PDF format. */
export function buildSubmit(spec: SubmitSpec, pairs: Array<[string, string]>, wholePdf?: Blob): BuiltRequest {
    const keep = spec.fields.length ? pairs.filter(([k]) => spec.fields.includes(k) || k.startsWith("fg_fill_")) : pairs;
    const format = spec.target === "WORKBOOK" ? "CSV" : spec.target === "DATABASE" ? "JSON" : spec.format;
    const headers = [...spec.headers];
    if (spec.get) {
        const q = encodePairs(keep, "HTML");
        return { method: "GET", url: `${spec.url}${spec.url.includes("?") ? "&" : "?"}${q}`, headers };
    }
    switch (format) {
        case "JSON": return { method: "POST", url: spec.url, headers: [...headers, ["Content-Type", "application/json"]], body: JSON.stringify(Object.fromEntries(keep)) };
        case "CSV": return { method: "POST", url: spec.url, headers: [...headers, ["Content-Type", "text/csv"]], body: csvRow(keep) };
        case "XFDF": return { method: "POST", url: spec.url, headers: [...headers, ["Content-Type", "application/vnd.adobe.xfdf"]], body: encodePairs(keep, "XFDF") };
        case "FDF": return { method: "POST", url: spec.url, headers: [...headers, ["Content-Type", "application/vnd.fdf"]], body: encodePairs(keep, "FDF") };
        case "PDF": return { method: "POST", url: spec.url, headers: [...headers, ["Content-Type", "application/pdf"]], body: wholePdf };
        default: return { method: "POST", url: spec.url, headers: [...headers, ["Content-Type", "application/x-www-form-urlencoded"]], body: encodePairs(keep, "HTML") };
    }
}

/** The value a "set field" action puts in: a quoted text or a number is itself; anything else is another field's name (its current value). */
export function evalSetValue(expr: string, valueOf: (name: string) => string): string {
    const e = expr.trim();
    const q = /^"(.*)"$/s.exec(e) ?? /^'(.*)'$/s.exec(e);
    if (q) return q[1]!;
    if (/^-?\d+(\.\d+)?$/.test(e)) return e;
    return valueOf(e);
}

export function beep(): void {
    try {
        const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        const ctx = new Ctx();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.frequency.value = 880;
        g.gain.value = 0.08;
        o.connect(g).connect(ctx.destination);
        o.start();
        o.stop(ctx.currentTime + 0.12);
        window.setTimeout(() => void ctx.close(), 300);
    } catch { /* silent */ }
}

/** Sends a built request; a sentence for a failure. */
export async function sendRequest(r: BuiltRequest): Promise<string> {
    let res: Response;
    try { res = await fetch(r.url, { method: r.method, headers: r.headers, body: r.body, credentials: "omit" }); }
    catch { throw new Error("The browser could not reach the address. The server has to be online and allow requests from this site (CORS)."); }
    if (!res.ok) throw new Error(`The server answered ${res.status}`);
    return `Sent. The server answered ${res.status}`;
}
